import { Alert, Box, Grid, MenuItem, Select, TextField, Typography } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { INumberTitle } from "ui/Scripts/source/models/common/numberTitle";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { ConfirmationDialogResult, EnumHelper, UserGroupAuthorizationLevel } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import { IUserGroupIntraModule, UserGroupSaveIntraModule } from "../../../models/intraModule/userGroupIntraModule";
import * as userGroupService from "../../../models/services/userGroupService";
import { ISessionState } from "../../../models/session/sessionState";
import * as storeActions from "../../../models/store/storeActions";
import { IUserListItem } from "../../../models/user/userListItem";
import { IUserGroupEditItem } from "../../../models/userGroup/userGroupEditItem";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { DialogTabs } from "../../framework/Dialog/DialogTabs";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { ListHeaderLine } from "../../framework/List/ListLine";
import { UserGroupEditDialogUserList } from "../UserGroupEditDialogUserList";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";

/* eslint-disable no-unused-vars */
export const enum UserGroupDialogMenuItem {
    General = "1",
    Users = "2",
    IntraModules = "4"
}
/* eslint-enable no-unused-vars */

//UserGroupEditDialog
export interface IUserGroupEditDialogProp {
    sessionState: ISessionState;
    defaultMenuItem: UserGroupDialogMenuItem;
    qrCodeUserGroup: boolean;
    companyId: string;
    userGroupId: string;
    classes?: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onSaved: () => void;
    onClose: (userGroupDataWasModified: boolean) => void;
    onNewSaved: (userGroupId: string) => void;
    onRefreshList: () => Promise<void>;
    onPrevious: () => void;
    onNext: () => void;
    onRefreshSessionState: () => void;
}

export interface IUserGroupEditDialogState {
    activeMenuItem: UserGroupDialogMenuItem;
    isLoading: boolean;
    userGroupDataWasModified: boolean;
    userGroup: IUserGroupEditItem;
    id: string;
    rowId: string;
    code: string;
    name: string;
    comment: string;
    authorizationLevel: number;
    qrCodeUserGroup: boolean;
    users: IUserListItem[];
    authorizationLevels: INumberTitle[];
    intraModules: IUserGroupIntraModule[];
}

export class UserGroupEditDialog extends React.Component<IUserGroupEditDialogProp, IUserGroupEditDialogState> {
    private orgStateHash: string = "";

    constructor(props: IUserGroupEditDialogProp) {
        super(props);
        this.state = {
            activeMenuItem: Base.isNullOrUndefined(props.defaultMenuItem) ? UserGroupDialogMenuItem.General : props.defaultMenuItem,
            isLoading: false,
            userGroupDataWasModified: false,
            userGroup: null,
            id: "",
            rowId: "",
            code: "",
            name: "",
            comment: "",
            authorizationLevel: 0,
            qrCodeUserGroup: props.qrCodeUserGroup,
            users: [],
            authorizationLevels: [],
            intraModules: []
        };
    }

    loadUserGroup = async (id: string) => {
        const props = this.props;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => userGroupService.getUserGroupEdit(props.companyId, id, props.qrCodeUserGroup), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const userGroup = result.userGroup;
        const hideIntraModulesTab = (result.intraModules.length < 1 || EnumHelper.isGreaterThan(userGroup.authorizationLevel, UserGroupAuthorizationLevel.SuperUser) || !props.sessionState.isSuperAdmin);
        const state = {
            ...this.state,
            userGroupDataWasModified: false,
            userGroup,
            id: userGroup.id,
            rowId: userGroup.rowId,
            code: userGroup.code,
            name: userGroup.name,
            comment: userGroup.comment,
            authorizationLevel: userGroup.authorizationLevel,
            qrCodeUserGroup: userGroup.qrCodeUserGroup,
            users: result.users,
            authorizationLevels: result.authorizationLevels,
            intraModules: result.intraModules,
            activeMenuItem: this.state.activeMenuItem === UserGroupDialogMenuItem.IntraModules && hideIntraModulesTab ? UserGroupDialogMenuItem.General : this.state.activeMenuItem,
        };
        this.orgStateHash = this.getSaveDataFromState(props, state).hash;
        this.setState(state);
    };

    componentDidMount(): void {
        this.loadUserGroup(this.props.userGroupId);
    }

    async componentDidUpdate(prevProps: IUserGroupEditDialogProp, prevState: IUserGroupEditDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        const prevDefaultMenuItem = Base.isNullOrUndefined(prevProps.defaultMenuItem) ? UserGroupDialogMenuItem.General : prevProps.defaultMenuItem;
        const defaultMenuItem = Base.isNullOrUndefined(props.defaultMenuItem) ? UserGroupDialogMenuItem.General : props.defaultMenuItem;
        if (prevProps.companyId === props.companyId && prevProps.userGroupId === props.userGroupId) return;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            if (!props.onRefreshList) {
                await props.onRefreshList();
            }
        }
        await this.loadUserGroup(props.userGroupId);
        if (prevDefaultMenuItem !== defaultMenuItem) {
            this.setState({
                activeMenuItem: defaultMenuItem
            });
        }
    }

    handleToggleActiveMenuItem = (userGroupDialogMenuItem: UserGroupDialogMenuItem) => {
        this.setState({ activeMenuItem: userGroupDialogMenuItem });
    };

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "xts") {
            this.setState({ code: value });
        } else if (name === "name") {
            this.setState({ name: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        } else if (name === "authorizationLevel") {
            this.setState({ authorizationLevel: value.toInteger() });
        }
    };
   
    handleChangeIntraModuleLevel = (event) => {
        const target = event.target;
        const value: string = target.value;
        const code: string = target.name;
        if (!code) return;
        const intraModules = this.state.intraModules.slice(0);
        const intraModule = intraModules.find(i => i.code === code);
        if (!intraModule) return;
        intraModule.level = value.toInteger();
        this.setState({ intraModules });
    };

    // #region Users
    handleUsersRemoved = (ids: string[]) => {
        if (!ids || ids.length < 1) return;
        this.setState({
            users: this.state.users.filter(i => ids.indexOf(i.id) < 0),
            userGroupDataWasModified: true
        });
    };
    
    handleUsersAdded = (newUsers: IUserListItem[]) => {
        if (!newUsers || newUsers.length < 1) return;
        this.setState({
            users: this.state.users.concat(newUsers),
            userGroupDataWasModified: true
        });
    };
    // #endregion User Groups

    getSaveDataFromState = (props: IUserGroupEditDialogProp, state: IUserGroupEditDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("name", state.name);
        result.append("comment", state.comment);
        result.append("authorizationLevel", state.authorizationLevel);
        result.append("qrCodeUserGroup", state.qrCodeUserGroup);
        // intraModules
        result.append("intraModules", state.intraModules.reduce((result, intraModule) => {
            result.push(new UserGroupSaveIntraModule(intraModule));
            return result;
        }, []));
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.UserCodeMustBeDefined));
                return resolve(false);
            }
            if (!state.name) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => userGroupService.saveUserGroupEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = this.getSaveDataFromState(props, { ...state, ...stateChange }).hash;
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const props = this.props;
        const result = await this.saveToDb();
        if (!result) return;
        const isNew = this.state.userGroup.isNew();
        if (!isNew) {
            if (!closeDialog) {
                this.loadUserGroup(result.id);
                props.onSaved();
                return;
            }
            props.onClose(true);
        } else {
            props.onNewSaved(result.id);
        }
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.userGroup.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveUserGroupParameter, props.sessionState.isSuperAdmin ? state.userGroup.code : state.userGroup.name), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await AppUtils.callService(() => userGroupService.removeUserGroup(state.userGroup.id, props.companyId));
            if (!result) return;
            props.onClose(true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(state.userGroupDataWasModified);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getSaveDataFromState(props, this.state).hash, this.orgStateHash,
            async () => !!await this.saveToDb(),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.userGroup) return null;
        const isNew = state.userGroup.isNew();
        const isSuperAdmin = props.sessionState.isSuperAdmin;
        return (
            <Dialog
                id="user-group-edit-dialog"
                maxWidth="md"
                maxHeight={isSuperAdmin ? "722px" : "654px"}
                title={isNew ? Translations.AddUserGroup : (Translations.EditUserGroup + ": " + (isSuperAdmin ? state.userGroup.code : state.userGroup.name))}
                dialogTabs={
                    <DialogTabs
                        activeTabId={state.activeMenuItem}
                        tabs={[
                            { id: UserGroupDialogMenuItem.General, title: Translations.BasicInformation },
                            { id: UserGroupDialogMenuItem.Users, title: Translations.Users },
                            {
                                id: UserGroupDialogMenuItem.IntraModules,
                                title: Translations.PermissionsForSystems,
                                hidden: state.intraModules.length < 1 || EnumHelper.isGreaterThan(state.authorizationLevel, UserGroupAuthorizationLevel.SuperUser) || !props.sessionState.isSuperAdmin || state.qrCodeUserGroup
                            }
                        ]}
                        onChangeTab={this.handleToggleActiveMenuItem}
                    />
                }
                body={
                    <>
                        {state.activeMenuItem === UserGroupDialogMenuItem.General &&
                            <>
                                {props.sessionState.isSuperAdmin &&
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.Code} required={isNew} margin={0}>
                                                <TextField type="text" name="xts" title={Translations.Code} inputProps={{ maxLength: 50 }} disabled={true} value={state.code} onChange={this.handleChange} />
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                }
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.Name} required margin={!props.sessionState.isSuperAdmin ? 0 : undefined}>
                                            <TextField type="text" name="name" title={Translations.Name} inputProps={{ maxLength: 50 }} disabled={false} value={state.name} onChange={this.handleChange} />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                {!state.qrCodeUserGroup &&
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.AuthorizationLevel} required>
                                                <Select name="authorizationLevel" title={Translations.AuthorizationLevel} value={state.authorizationLevel.toString(10)} onChange={this.handleChange}>
                                                    {state.authorizationLevels.map((authorizationLevel) =>
                                                        <MenuItem key={authorizationLevel.number} value={authorizationLevel.number.toString(10)}>{EnumHelper.UserGroupAuthorizationLevelToStr(authorizationLevel.number)}</MenuItem>
                                                    )}
                                                </Select>
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                }
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.Description}>
                                            <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 500 }} multiline minRows={5} value={state.comment} required={true} onChange={this.handleChange} />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                {!!state.qrCodeUserGroup &&
                                    <Alert sx={{ marginTop: "10px", whiteSpace: "pre-line" }} severity="info">{Translations.UserModuleInfoQrCodeUserGroup}</Alert>
                                }
                            </>
                        }
                        {state.activeMenuItem === UserGroupDialogMenuItem.Users &&
                            <UserGroupEditDialogUserList
                                isReadOnly={isNew}
                                userGroupId={state.id}
                                qrCodeUserGroup={state.qrCodeUserGroup}
                                companyId={props.companyId}
                                users={state.users}
                                onRemoved={this.handleUsersRemoved}
                                onAdded={this.handleUsersAdded}
                                onRefreshSessionState={props.onRefreshSessionState}
                            />
                        }
                        {state.activeMenuItem === UserGroupDialogMenuItem.IntraModules &&
                            <>
                                <ListHeaderLine
                                    columns={[
                                        { size: 7, column: "name", content: Translations.Name },
                                        { size: 5, column: "permission", content: Translations.Permission },
                                    ]}
                                />
                                {state.intraModules.map((intraModule) =>
                                    <Grid container key={intraModule.code}>
                                        <Grid item sm={7 } sx={{ padding: ".5rem" }} display="flex" alignItems="center">
                                            <img className="icon" alt="..." src={appConfig.rootUrl + "Content/" + intraModule.code + ".png"} />
                                            <Typography variant="body2" sx={{ marginLeft: ".5rem" }}>{intraModule.name}</Typography>
                                        </Grid>
                                        <Grid item sm={5} sx={{ padding: ".5rem" }} display="flex" justifyContent="flex-end">
                                            <Select name={intraModule.code} title={Translations.AuthorizationLevel} value={intraModule.level.toString(10)} onChange={this.handleChangeIntraModuleLevel}>
                                                {intraModule.permissions.map((permission) =>
                                                    <MenuItem key={permission.number} value={permission.number.toString(10)}>{permission.title}</MenuItem>
                                                )}
                                            </Select>
                                        </Grid>
                                    </Grid>
                                )}
                            </>
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                            createUser={!isNew ? state.userGroup.createUser : null}
                            modifyUser={!isNew ? state.userGroup.modifyUser : null}
                        />
                        <DialogButtons
                            isNew={isNew}
                            saveTitle={isNew && state.activeMenuItem === UserGroupDialogMenuItem.General ? Translations.Next : Translations.Save}
                            onCancel={this.handleCancel}
                            onRemove={state.userGroup && !isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}/>
        );
    }
}